<script setup lang="ts">
import ShipTable from '@/components/ship/ShipTable.vue'
import ShipTableMenu from '@/components/ship/ShipTableMenu.vue'
import { useShipStore } from '@/stores/ship'
import { storeToRefs } from 'pinia'
import CreateUpdateShipDialog from '@/components/ship/CreateUpdateShipDialog.vue'
import { useProjectStore } from '@/stores/project'
import { computed, onMounted } from 'vue'
import { useShipAPIStore } from '@/stores/shipAPI'
import ImportShipDialog from '@/components/ship-importer/ImportShipDialog.vue'
import { DeleteDialog } from '@haskoning/vue-component-library'
import { useRoute } from 'vue-router'
import ShipTableTitle from '@/components/ship/ShipTableTitle.vue'
import type { ShipType } from '@/stores/type/ship.type'
const route = useRoute()

const shipStore = useShipStore()
const shipAPIStore = useShipAPIStore()
const projectStore = useProjectStore()

const { currentItem: currentProjectItem } = storeToRefs(projectStore)
const {
  currentItem: currentShip,
  selectedItems,
  items,
  canDeleteShip,
  fields,
  deleteDialog,
  deleteLoading
} = storeToRefs(shipStore)

const projectId = computed(() => {
  return route.params.id
})

onMounted(async () => {
  await shipStore.fetchOptions()
  if (projectId.value) {
    await shipStore.fetchItems({ search: projectId.value })
  }
  try {
    await shipAPIStore.fetchOptions()
    await shipAPIStore.fetchShipItems()
  } catch (error) {
    console.log(error)
  }
})

const addShip = () => {
  if (fields) {
    const newShip = ref<ShipType>(shipStore.createNewShip(currentProjectItem.value.id))
    shipStore.setCurrentItem(newShip.value)
    shipStore.openCreateUpdateDialog()
  }
}

const saveShip = async (): Promise<void> => {
  if (currentShip.value?.id) {
    await shipStore.update(currentShip.value)
  } else {
    await shipStore.create(currentShip.value)
  }
  shipStore.closeCreateUpdateDialog()
}
</script>

<template>
  <v-card elevation="0" class="px-8 py-6">
    <ShipTableTitle
      :can-add="shipStore.canAddShip"
      :disable-add="!(currentProjectItem?.id && fields)"
      :disable-import="!currentProjectItem?.id"
      @add-item="addShip()"
      @import-item="shipAPIStore.openCreateUpdateDialog"
    ></ShipTableTitle>
    <template v-if="false">
      <div class="d-flex justify-center align-center fill-height">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </div>
    </template>
    <template v-else>
      <v-card border="opacity-35 sm" class="mt-2 mx-auto" variant="text">
        <ShipTableMenu
          :selected-items="selectedItems"
          :filtered-items="items"
          :can-add-item="shipStore.canAddShip"
          :can-delete-item="canDeleteShip"
          :show-delete-button="true"
          @update-selected-items="shipStore.updateSelectedItems"
          @delete-selected-items="shipStore.deleteSelectedItems"
        />
        <ShipTable />
      </v-card>
    </template>
    <CreateUpdateShipDialog v-if="currentShip" @save="saveShip" />
    <ImportShipDialog v-if="fields" />
    <DeleteDialog
      v-model="deleteDialog"
      :loading="deleteLoading"
      :bulk-delete="true"
      :items="selectedItems"
      @bulk-delete="shipStore.bulkDelete($event)"
      @submitted-and-closed="shipStore.clearSelectedItems"
    />
  </v-card>
</template>

<style scoped></style>
